import { useState } from "react";
import { Archive, Copy, LogOut, Pencil } from "lucide-react";
import { mutate } from "swr";
import { DropdownMenuItem, DropdownMenuSeparator } from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import {
  EntityOverviewHeader,
  OverviewRow,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { OverflowMenu } from "~/pages/manifold-nextgen/components/OverflowMenu";
import {
  PILL_AVATAR_SIZE,
  PILL_GLYPH_SIZE,
  PreviewRow,
} from "~/pages/manifold-nextgen/components/PreviewRow";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import { useCopyToClipboard } from "~/pages/manifold-nextgen/hooks/useCopyToClipboard";
import { useIsShared } from "~/pages/manifold-nextgen/hooks/useIsShared";
import { useProjectEntityRemoval } from "~/pages/manifold-nextgen/hooks/useProjectEntityRemoval";
import { useProjectExit } from "~/pages/manifold-nextgen/hooks/useProjectExit";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { ROUTE_NEXTGEN_VIEW, nextGenDatasetDetailPath } from "~/router/constants";
import { projectPath, projectRouteId, updateProject } from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { LeaveProjectDialog } from "./LeaveProjectDialog";
import { ProjectAccessBadge } from "./ProjectAccessBadge";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { ProjectEditDialog } from "./ProjectEditDialog";
import { ProjectIcon } from "./ProjectIcon";
import { useProjectContext } from "./useProjectContext";

export const ProjectOverview = () => {
  const { projectId, project, isOwner, attachedAgents, attachedDatasets } = useProjectContext();
  const { collaborators, accessLevels } = useProjectShare(projectId);
  const { showError } = useToast();
  const copyToClipboard = useCopyToClipboard();
  const { isFavorite, toggleFavorite } = useFavorites("project-favorites", [project], (p) => p.id);
  const { handleArchive, handleLeave } = useProjectExit(projectId, project.name);
  const [editOpen, setEditOpen] = useState(false);
  const [addDatasetsOpen, setAddDatasetsOpen] = useState(false);
  const [addAgentsOpen, setAddAgentsOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const memberCount = collaborators
    ? buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels).length
    : undefined;

  const { handleRemoveDataset, handleRemoveAgent, isRemovingDataset, isRemovingAgent } =
    useProjectEntityRemoval(projectId, attachedDatasets, attachedAgents);

  const handleSaveDescription = async (next: string) => {
    try {
      await updateProject(projectId, { description: next });
      await mutate(projectPath(projectId));
    } catch (e) {
      showError("Failed to update description");
      throw e;
    }
  };

  const isShared = useIsShared(collaborators, accessLevels);

  const agents = attachedAgents ?? [];
  const datasets = attachedDatasets ?? [];

  const rows: OverviewRow[] = [
    {
      label: "Datasets",
      content: (
        <PreviewRow
          isLoading={attachedDatasets === undefined}
          isRemoving={isRemovingDataset}
          onAdd={isOwner ? () => setAddDatasetsOpen(true) : undefined}
          addLabel="Datasets"
          items={datasets.map((d) => ({
            key: d.id,
            label: d.title,
            leading: <DatasetIcon dataset={d} size={PILL_GLYPH_SIZE} />,
            to: nextGenDatasetDetailPath(d.id),
            onRemove: isOwner ? () => handleRemoveDataset(d.id) : undefined,
          }))}
        />
      ),
    },
    {
      label: "Agents",
      content: (
        <PreviewRow
          isLoading={attachedAgents === undefined}
          isRemoving={isRemovingAgent}
          onAdd={isOwner ? () => setAddAgentsOpen(true) : undefined}
          addLabel="Agents"
          items={agents.map((a) => ({
            key: a.id,
            label: a.display_name,
            leading: <AgentAvatar agent={a} size={PILL_AVATAR_SIZE} />,
            to: `${ROUTE_NEXTGEN_VIEW}/agents/${a.id}`,
            onRemove: isOwner ? () => handleRemoveAgent(a.id) : undefined,
          }))}
        />
      ),
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl pt-4">
        <EntityOverviewHeader
          tiles={[]}
          rows={rows}
          icon={<ProjectIcon project={project} size={36} />}
          title={project.name}
          type="Project"
          updatedAt={project.updated_at}
          roleBadge={<ProjectAccessBadge accessLevel={project.access_level} />}
          isShared={isShared}
          actions={
            <>
              <FavoriteButton
                favorite={isFavorite(project.id)}
                onToggle={() => toggleFavorite(project.id)}
                outlined
              />
              <OverflowMenu label="project actions">
                {isOwner && (
                  <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                    <Pencil className="size-4" aria-hidden="true" />
                    Edit name and appearance
                  </DropdownMenuItem>
                )}
                {/* The short_id is what the URL carries, so it is the id a
                    user pasting this into a ticket actually wants. */}
                <DropdownMenuItem
                  onSelect={() => void copyToClipboard(projectRouteId(project), "Project ID")}
                >
                  <Copy className="size-4" aria-hidden="true" />
                  Copy project ID
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setLeaveOpen(true)}>
                  <LogOut className="size-4" aria-hidden="true" />
                  Leave project
                </DropdownMenuItem>
                {isOwner && (
                  <DropdownMenuItem variant="destructive" onSelect={() => setArchiveOpen(true)}>
                    <Archive className="size-4" aria-hidden="true" />
                    Archive project
                  </DropdownMenuItem>
                )}
              </OverflowMenu>
            </>
          }
        >
          <EditableDescription
            heading="Description"
            value={project.description}
            canEdit={isOwner}
            onSave={handleSaveDescription}
          />
        </EntityOverviewHeader>
      </div>
      <LeaveProjectDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        projectName={project.name}
        onConfirm={handleLeave}
      />
      {isOwner && (
        <>
          <DeleteProjectDialog
            open={archiveOpen}
            onOpenChange={setArchiveOpen}
            projectName={project.name}
            memberCount={memberCount}
            onConfirm={handleArchive}
          />
          {editOpen && <ProjectEditDialog project={project} open onOpenChange={setEditOpen} />}
          <ProjectDatasetsDialog
            projectId={projectId}
            open={addDatasetsOpen}
            onOpenChange={setAddDatasetsOpen}
            attachedIds={datasets.map((d) => d.id)}
          />
          <ProjectAgentsDialog
            projectId={projectId}
            open={addAgentsOpen}
            onOpenChange={setAddAgentsOpen}
            attached={agents}
          />
        </>
      )}
    </div>
  );
};
